import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useLogisticsProviders } from '@/modules/logistics/hooks';
import {
  getLogisticsOrder,
  saveLogisticsOrder,
} from '@/modules/logistics/services';
import { getReservation } from '@/modules/reservations/services';
import { listItems } from '@/modules/inventory/services';
import {
  LOGISTICS_ORDER_STATUS_LABELS,
  LOGISTICS_ORDER_TYPE_LABELS,
  type LogisticsOrderInput,
  type LogisticsOrderStatus,
  type LogisticsOrderType,
} from '@/modules/logistics/types';
import { validateLogisticsOrderInput } from '@/modules/logistics/utils/validation';
import { parseLogisticsOrderPrefill } from '@/modules/logistics/utils/logisticsUrl';
import type { InventoryItem } from '@/modules/inventory/types';
import type { Reservation } from '@/modules/reservations/types';
import { modulePaths } from '@/routes/paths';
import { EntityAuditTrail } from '@/shared/components/EntityAuditTrail';
import { useAuth } from '@/shared/hooks/useAuth';

const defaultInput: LogisticsOrderInput = {
  reservationId: null,
  type: 'delivery',
  status: 'pending',
  scheduledDate: '',
  providerId: null,
  address: '',
  contactName: '',
  contactPhone: '',
  notes: '',
  inventoryItemIds: [],
};

const WRITABLE_STATUSES: LogisticsOrderStatus[] = [
  'pending',
  'scheduled',
  'in_transit',
  'delivered',
  'returned',
  'cancelled',
];

export function LogisticsOrderFormPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { profile, hasPermission } = useAuth();
  const canWrite = hasPermission('logistics.write');
  const { providers } = useLogisticsProviders();

  const [form, setForm] = useState<LogisticsOrderInput>(defaultInput);
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [linkedReservation, setLinkedReservation] = useState<Reservation | null>(
    null,
  );
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void listItems().then(setItems);
  }, []);

  useEffect(() => {
    if (!isNew) return;
    const prefill = parseLogisticsOrderPrefill(searchParams);
    setForm((prev) => ({
      ...prev,
      ...(prefill.reservationId ? { reservationId: prefill.reservationId } : {}),
      ...(prefill.type ? { type: prefill.type } : {}),
      ...(prefill.scheduledDate ? { scheduledDate: prefill.scheduledDate } : {}),
    }));
  }, [isNew, searchParams]);

  useEffect(() => {
    if (!form.reservationId) {
      setLinkedReservation(null);
      return;
    }
    void getReservation(form.reservationId).then((res) => {
      setLinkedReservation(res);
      if (res && isNew) {
        setForm((prev) => ({
          ...prev,
          inventoryItemIds:
            prev.inventoryItemIds.length > 0
              ? prev.inventoryItemIds
              : [...res.inventoryItemIds],
          contactName: prev.contactName || res.clientName,
        }));
      }
    });
  }, [form.reservationId, isNew]);

  useEffect(() => {
    if (isNew || !id) return;
    setLoading(true);
    void getLogisticsOrder(id)
      .then((order) => {
        if (!order) {
          setError('Orden no encontrada.');
          return;
        }
        setForm({
          reservationId: order.reservationId,
          type: order.type,
          status: order.status,
          scheduledDate: order.scheduledDate,
          providerId: order.providerId,
          address: order.address,
          contactName: order.contactName,
          contactPhone: order.contactPhone,
          notes: order.notes,
          inventoryItemIds: order.inventoryItemIds,
        });
      })
      .catch(() => setError('Error al cargar la orden.'))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  function toggleItem(itemId: string) {
    if (!canWrite) return;
    setForm((prev) => {
      const has = prev.inventoryItemIds.includes(itemId);
      return {
        ...prev,
        inventoryItemIds: has
          ? prev.inventoryItemIds.filter((x) => x !== itemId)
          : [...prev.inventoryItemIds, itemId],
      };
    });
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canWrite || !profile) return;
    const validation = await validateLogisticsOrderInput(form);
    if (validation) {
      setError(validation);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const saved = await saveLogisticsOrder(
        form,
        profile.uid,
        isNew ? undefined : id,
      );
      void navigate(`${modulePaths.logistics}/${saved.id}`, { replace: true });
    } catch {
      setError('No se pudo guardar la orden.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <p className="auth-muted">Cargando…</p>;

  return (
    <section>
      <p>
        <Link to={modulePaths.logistics}>← Volver al listado</Link>
      </p>
      <h1>{isNew ? 'Nueva orden logística' : 'Editar orden logística'}</h1>

      <form className="inventory-panel" onSubmit={handleSubmit}>
        <div className="users-form-grid">
          <label className="auth-label">
            Tipo *
            <select
              disabled={!canWrite}
              value={form.type}
              onChange={(e) =>
                setForm({ ...form, type: e.target.value as LogisticsOrderType })
              }
            >
              {(Object.keys(LOGISTICS_ORDER_TYPE_LABELS) as LogisticsOrderType[]).map(
                (t) => (
                  <option key={t} value={t}>
                    {LOGISTICS_ORDER_TYPE_LABELS[t]}
                  </option>
                ),
              )}
            </select>
          </label>
          <label className="auth-label">
            Estado
            <select
              disabled={!canWrite}
              value={form.status}
              onChange={(e) =>
                setForm({
                  ...form,
                  status: e.target.value as LogisticsOrderStatus,
                })
              }
            >
              {WRITABLE_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {LOGISTICS_ORDER_STATUS_LABELS[s]}
                </option>
              ))}
            </select>
          </label>
          <label className="auth-label">
            Fecha programada *
            <input
              type="date"
              required
              disabled={!canWrite}
              value={form.scheduledDate}
              onChange={(e) =>
                setForm({ ...form, scheduledDate: e.target.value })
              }
            />
          </label>
          <label className="auth-label">
            Proveedor
            <select
              disabled={!canWrite}
              value={form.providerId ?? ''}
              onChange={(e) =>
                setForm({
                  ...form,
                  providerId: e.target.value || null,
                })
              }
            >
              <option value="">— Sin asignar —</option>
              {providers
                .filter((p) => p.active)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
            </select>
          </label>
          <label className="auth-label">
            Reserva (opcional)
            <input
              disabled={!canWrite}
              placeholder="ID de reserva"
              value={form.reservationId ?? ''}
              onChange={(e) =>
                setForm({
                  ...form,
                  reservationId: e.target.value || null,
                })
              }
            />
          </label>
        </div>

        {linkedReservation ? (
          <p className="auth-muted">
            Reserva vinculada:{' '}
            <Link to={`${modulePaths.reservations}/${linkedReservation.id}`}>
              {linkedReservation.title}
            </Link>
          </p>
        ) : null}

        <label className="auth-label">
          Dirección
          <input
            disabled={!canWrite}
            value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
          />
        </label>
        <div className="users-form-grid">
          <label className="auth-label">
            Contacto
            <input
              disabled={!canWrite}
              value={form.contactName}
              onChange={(e) => setForm({ ...form, contactName: e.target.value })}
            />
          </label>
          <label className="auth-label">
            Teléfono
            <input
              disabled={!canWrite}
              value={form.contactPhone}
              onChange={(e) => setForm({ ...form, contactPhone: e.target.value })}
            />
          </label>
        </div>
        <label className="auth-label">
          Notas
          <textarea
            rows={2}
            disabled={!canWrite}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </label>

        <fieldset className="inventory-fieldset">
          <legend>Artículos</legend>
          <ul className="reservation-item-picker">
            {items.map((item) => (
              <li key={item.id}>
                <label className="auth-label auth-label--row">
                  <input
                    type="checkbox"
                    disabled={!canWrite}
                    checked={form.inventoryItemIds.includes(item.id)}
                    onChange={() => toggleItem(item.id)}
                  />
                  <span>
                    <code>{item.sku}</code> — {item.name}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </fieldset>

        {error ? <p className="auth-error">{error}</p> : null}

        {canWrite ? (
          <div className="inventory-actions">
            <button type="submit" className="auth-button" disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        ) : null}
      </form>

      {!isNew && id ? (
        <EntityAuditTrail entityType="logisticsOrder" entityId={id} />
      ) : null}
    </section>
  );
}
